import { TaskScheduler } from './TaskScheduler'
import type { AbsTask } from '@core/transmission/AbsTask'
import { TransmissionDataBusProxy } from '@core/transmission/TransmissionDataBusProxy'
import type { TransmissionNoticeData } from '../databus/DataSyncManager'
import { TaskState } from './ITask'
import type { IDevice } from '../devices/IDevice'
import { INSTANCE as ToastManager } from '@/core/ToastManager'
import { i18n } from '@/i18n'

const { t } = i18n.global

export interface TaskManagerChangeListener {
  onAdd(task: AbsTask): void
  onUpdated(task: AbsTask, needShow?: boolean): void
  onExceedMaximumFileSize(): void
}

const PARALLEL_TASK_NUMBER = 3

const unfinishedTaskStates = [TaskState.FAILED, TaskState.GOING, TaskState.PAUSE, TaskState.WAITING]

export abstract class AbsFileTransmissionManager<T extends AbsTask> {
  protected _taskScheduler: TaskScheduler<T>
  protected _taskManagerChangeListener = new Set<TaskManagerChangeListener>()
  constructor() {
    this._taskScheduler = new TaskScheduler(PARALLEL_TASK_NUMBER)

    this._taskScheduler.setSchedulerChangeListener({
      onAdded: (task: T) => {
        this.#notifyTaskAdd(task)
      },
      onUpdated: (task: T, needShow) => {
        this.#notifyTaskChange(task, needShow)
      },
      onSuccess: (task: T) => {
        this._onSuccess(task)
      },
      onExceedMaximumFileSize: () => {
        this.#notifyExceedMaximumFileSize()
      },
    })

    TransmissionDataBusProxy.getInstance().addNoticeListener({
      onAdded: (messages: TransmissionNoticeData[]) => {
        for (const message of messages) {
          this.#noticeUpdate(message)
        }
      },
      onUpdated: (messages: TransmissionNoticeData[]) => {
        for (const message of messages) {
          this.#noticeUpdate(message)
        }
      },
      onRemoved: () => {
        //do nothing
      },
      onInitialized(): void {
        // TODO: to be implemented
      },
    })
  }

  protected abstract _onSuccess(task: T): void

  protected _isDisableAppendTask(device: IDevice) {
    if (device.isDisabledTransmissionFile) {
      ToastManager.show(t('owork_base.device_version_tips', { device: device.model }))
    }

    return device.isDisabledTransmissionFile
  }

  addTaskManagerChangeListener(listener: TaskManagerChangeListener): void {
    if (this._taskManagerChangeListener.has(listener)) return
    this._taskManagerChangeListener.add(listener)
  }

  removeTaskManagerChangeListener(listener: TaskManagerChangeListener): void {
    this._taskManagerChangeListener.delete(listener)
  }

  resumeAllPausedTask(): void {
    this._taskScheduler.resumeAllPausedTasks()
  }

  startAllTask(): void {
    this._taskScheduler.resumeAllPausedTasks()
  }

  /**
   * pause all file upload task
   */
  pauseAllTask(): void {
    this._taskScheduler.pauseAllTask()
  }

  /**
   * delete all file upload task
   */
  removeAllTask(): void {
    this._taskScheduler.removeAllTask()
  }

  restartTaskById(taskId: string): void {
    const task = this.getTaskById(taskId)

    task && this._taskScheduler.restartTask(task)
  }

  removeTaskById(taskId: string): void {
    const task = this.getTaskById(taskId)

    task && this._taskScheduler.removeTask(task)
  }

  pauseTaskById(taskId: string): void {
    const task = this.getTaskById(taskId)

    task && this._taskScheduler.pauseTask(task)
  }

  resumeTaskById(taskId: string): void {
    const task = this.getTaskById(taskId)

    task && this._taskScheduler.resumeTask(task)
  }

  getAllTask(): T[] {
    return this._taskScheduler.getAllTask()
  }

  hasUnfinishedTask(): boolean {
    return this._taskScheduler.includesTaskByState(unfinishedTaskStates)
  }

  getTaskTotalNumber(): number {
    return this._taskScheduler.size
  }

  getTaskByNoticeId(id: string): T | undefined {
    return this._taskScheduler.getAllTask().find((task) => task.noticeId === id)
  }

  getTaskById(taskId: string): T | undefined {
    return this.getAllTask().find((task) => task.id === taskId)
  }

  #notifyTaskAdd(task: T): void {
    for (const listener of this._taskManagerChangeListener) {
      listener.onAdd(task)
    }
  }

  #notifyTaskChange(task: T, needShow?: boolean): void {
    for (const listener of this._taskManagerChangeListener) {
      listener.onUpdated(task, needShow)
    }
  }

  #notifyExceedMaximumFileSize(): void {
    for (const listener of this._taskManagerChangeListener) {
      listener.onExceedMaximumFileSize()
    }
  }

  #noticeUpdate(message: TransmissionNoticeData) {
    let task: T | undefined

    if (
      message.webClientId === TransmissionDataBusProxy.getInstance().webClientId &&
      (task = this.getTaskByNoticeId(message.id))
    ) {
      console.log(`TRANSMISSION onUpdated notice update message: ${JSON.stringify(message)}`)

      task.updateInfo(message, false)
    }
  }

  /**
   * For test only. **DO NOT** use in production version.
   *
   * @deprecated
   * @internal
   */
  setTaskScheduler(taskScheduler: any) {
    this._taskScheduler = taskScheduler
  }
}
